import React, { ReactElement, useCallback, useMemo } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { MemoProcessIcon } from 'components/common/ProcessIcon';
import { ProcessIconSize } from 'components/common/ProcessIcon/types';
import { ProcessSelectProps } from './types';
import {
  PROCESS_SELECT_FORM_CONTROL_SX,
  PROCESS_SELECT_SX,
  PROCESS_MENU_ITEM_SX,
  PROCESS_LABEL_SX,
} from './styles';

function ProcessSelect({
  processes,
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
}: ProcessSelectProps): ReactElement {
  const intl = useIntl();

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value;
      onChange(event as unknown as React.MouseEvent<HTMLElement>, newValue || null);
    },
    [onChange]
  );

  const renderValue = useCallback(
    (selectedId: string) => {
      const selectedProcess = processes.find((p) => p._id === selectedId);
      if (!selectedProcess) return null;

      return (
        <Box sx={PROCESS_MENU_ITEM_SX}>
          <MemoProcessIcon
            color={selectedProcess.color}
            code={selectedProcess.code}
            size={ProcessIconSize.small}
          />
          <Box component="span" sx={PROCESS_LABEL_SX}>
            {selectedProcess.label}
          </Box>
        </Box>
      );
    },
    [processes]
  );

  const menuItems = useMemo(
    () =>
      processes.map((process) => (
        <MenuItem key={process._id} value={process._id} sx={PROCESS_MENU_ITEM_SX}>
          <MemoProcessIcon
            color={process.color}
            code={process.code}
            size={ProcessIconSize.small}
          />
          <Box component="span" sx={PROCESS_LABEL_SX}>
            {process.label}
          </Box>
        </MenuItem>
      )),
    [processes]
  );

  const label = formatTranslation(intl, CommonTranslationKeys.process);
  const displayLabel = `${label}${required ? ' *' : ''}`;

  return (
    <FormControl
      sx={PROCESS_SELECT_FORM_CONTROL_SX}
      error={error}
      disabled={disabled}
    >
      <InputLabel id="process-select-label">{displayLabel}</InputLabel>
      <Select
        labelId="process-select-label"
        id="process-select"
        value={value || ''}
        label={displayLabel}
        onChange={handleChange}
        renderValue={renderValue}
        sx={PROCESS_SELECT_SX}
      >
        {menuItems}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export const MemoProcessSelect = React.memo(ProcessSelect);
